/**
 * React Query Configuration
 * 
 * Sets up TanStack Query (React Query) with custom configuration for the application.
 * Includes default options for queries and mutations, error handling, and caching.
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Smart retry function that retries based on error type and status code
 * @param {number} failureCount - Number of failures so far
 * @param {Error} error - The error that occurred
 * @returns {boolean} Whether to retry or not
 */
const shouldRetry = (failureCount, error) => {
  // Don't retry more than 3 times
  if (failureCount >= 3) {
    return false;
  }

  // Don't retry if offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return false;
  }

  // Don't retry on client errors (4xx) except for rate limiting (429) and timeout (408)
  if (error?.response?.status) {
    const status = error.response.status;
    
    // Retry on rate limiting (429) and timeout (408)
    if (status === 429 || status === 408) {
      return true;
    }
    
    // Don't retry on other 4xx errors (bad request, unauthorized, forbidden, not found, etc.)
    if (status >= 400 && status < 500) {
      return false;
    }
    
    // Retry on 5xx server errors
    if (status >= 500) {
      return true;
    }
  }

  // Retry on network errors
  const errorMessage = error?.message?.toLowerCase() || '';
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('timeout') ||
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('unable to connect')
  ) {
    return true;
  }

  // Default: don't retry
  return false;
};

/**
 * Calculate retry delay with exponential backoff
 * @param {number} attemptIndex - Current attempt index (0-based)
 * @returns {number} Delay in milliseconds
 */
const getRetryDelay = (attemptIndex) => {
  // Exponential backoff: 1s, 2s, 4s
  return Math.min(1000 * Math.pow(2, attemptIndex), 10000);
};

// Create a query client with custom defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time before data is considered stale (15 minutes - increased from 5 to reduce refetches)
      staleTime: 20 * 60 * 1000,
      // Time before inactive queries are garbage collected (10 minutes)
      gcTime: 10 * 60 * 1000, // Previously cacheTime
      // Smart retry: retry up to 3 times based on error type
      retry: shouldRetry,
      // Retry delay with exponential backoff
      retryDelay: getRetryDelay,
      // DISABLED: Don't refetch on window focus by default (reduces API calls by ~70%)
      // Individual queries can override this for critical data (dashboard, wallet balance)
      refetchOnWindowFocus: false,
      // ENABLED: Refetch on reconnect to recover from offline state
      refetchOnReconnect: true,
      // DISABLED: Don't refetch on mount if data exists (use cached data)
      refetchOnMount: false,
    },
    mutations: {
      // Retry failed mutations 1 time (only for network/server errors)
      retry: (failureCount, error) => {
        // Don't retry mutations more than once
        if (failureCount >= 1) {
          return false;
        }

        // Don't retry if offline
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          return false;
        }

        // Only retry on network errors or 5xx server errors
        if (error?.response?.status) {
          const status = error.response.status;
          // Retry on rate limiting (429), timeout (408), or server errors (5xx)
          return status === 429 || status === 408 || status >= 500;
        }

        // Retry on network errors
        const errorMessage = error?.message?.toLowerCase() || '';
        return (
          errorMessage.includes('network') ||
          errorMessage.includes('timeout') ||
          errorMessage.includes('failed to fetch') ||
          errorMessage.includes('connection')
        );
      },
      retryDelay: getRetryDelay,
    },
  },
});

